import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function AddingACustomer() {
  return (
    <>
      <p>
        A customer is a company whose developers use Nexus. Adding one takes a minute; making it useful takes three more
        steps: a proxy in its AWS account, a way for its developers to sign in, and a rate plan. Only super admins can add
        customers.
      </p>

      <h2>Add the customer</h2>
      <ol>
        <li>
          Open <ScreenLink href="/customers">Customers</ScreenLink> and choose <strong>Add customer</strong>.
        </li>
        <li>
          Enter the <strong>id</strong>, a <strong>slug</strong> and the <strong>name</strong>. The id and slug are
          lowercase letters, numbers and hyphens, up to 64 characters. The name is what everyone sees, up to 200
          characters.
        </li>
        <li>Save. An id that already exists is refused.</li>
      </ol>
      <Callout>
        The id cannot be changed later. If the customer's developers will sign in with an identity provider (OIDC), the id
        must equal the value their tokens carry in the org claim (<code>org_id</code> by default). Agree it with the
        customer first.
      </Callout>

      <h2>Then set it up</h2>
      <ol>
        <li>
          <strong>Install the proxy</strong> in the customer's AWS account with the{" "}
          <ScreenLink href="/installer">Installer</ScreenLink>, or register an installation by hand on the customer's{" "}
          <strong>AWS accounts</strong> tab. See <HelpLink to="installer" /> and <HelpLink to="installations" />.
        </li>
        <li>
          <strong>Decide how developers sign in</strong> on the <strong>Sign-in</strong> tab: their AWS sign-in (the
          default) or the company's identity provider. API keys and the key policy are on the <strong>Keys</strong> tab. See{" "}
          <HelpLink to="users-teams-and-sign-in" />.
        </li>
        <li>
          <strong>Set up billing</strong> on the <strong>Billing</strong> tab: assign a rate plan and switch the customer to
          billed from a month. Until then the customer is <em>not billed</em>: its usage is metered and reported but never
          invoiced. See <HelpLink to="rate-plans" section="billing-mode" />.
        </li>
      </ol>
      <p>
        A new customer already has an empty month open for last month and this month. The worker opens them by itself; you
        never create a month by hand.
      </p>

      <h2 id="status">Status</h2>
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>What it means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Active</td>
            <td>Normal. Developers can use the proxy.</td>
          </tr>
          <tr>
            <td>Suspended</td>
            <td>
              Every developer, key and installation of the customer is refused on new requests. Usage already on its way is
              still recorded, and all history is kept. <strong>Restore</strong> makes it active again.
            </td>
          </tr>
          <tr>
            <td>Removed</td>
            <td>
              Final. Nothing of the customer's is accepted any more, and only super admins can still see it. It is set
              through the API; this app offers no way to remove a customer or bring one back.
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>Suspend</strong> and <strong>Restore</strong> are on the customer's page and ask you to confirm. Use
        Suspend for an unpaid account or a security concern; it is fully reversible.
      </p>

      <h2 id="settings">Settings</h2>
      <p>The customer's <strong>Settings</strong> tab holds its data rules:</p>
      <table>
        <thead>
          <tr>
            <th>Setting</th>
            <th>Range</th>
            <th>What it does</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Prompt logging</td>
            <td>On / off</td>
            <td>
              Off discards prompt and response text as it arrives, so it is never stored. Text is stored only when this and
              the installation's own prompt-logging switch are both on.
            </td>
          </tr>
          <tr>
            <td>Prompt retention</td>
            <td>1–3,650 days</td>
            <td>How long stored prompt text is kept.</td>
          </tr>
          <tr>
            <td>Usage retention</td>
            <td>30–3,650 days</td>
            <td>
              How long individual usage events are kept. Daily totals are kept for ever, so reports and invoices are
              unaffected.
            </td>
          </tr>
          <tr>
            <td>Assistant</td>
            <td>On / off (super-admin only)</td>
            <td>
              On by default. Off hides the Assistant from the customer's admins on their proxy; AllCode's own Assistant
              conversations about the customer are unaffected.
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        Retention changes apply at the next hourly clean-up, not at once. Raw usage of a month that is not yet closed, or
        was closed recently, is kept whatever the setting, so a disputed invoice can always be checked.
      </p>

      <Related to={["users-teams-and-sign-in", "installations", "rate-plans", "installer"]} />
    </>
  );
}
